import React from "react";
import { Autocomplete, Button, TextField, type Option } from "../../../shared/components";
import { useAppSelector } from "../../../store";
import { aggregateCycles, nodeLabel, type GroupSummary } from "../../../store/profiling/selectors";
import {
    EMPTY_FILTERS,
    hasActiveFilters,
    eventFilterOptions,
    fileFilterOptions,
    type EventOption,
    type ProfilingFilters as Filters,
} from "../../../store/profiling/filtering";
import { traceKindLabel } from "../traceFormat";
import CodeBlock from "../CodeBlock";

type Props = {
    filters: Filters;
    onChange: (filters: Filters) => void;
    /** All retained passes (unfiltered) — drives option derivation and the total count. */
    groups: GroupSummary[];
    /** Passes remaining after the filters are applied. */
    shownCount: number;
};

const eventLabel = (o: EventOption) => (o.name ? `${traceKindLabel(o.kind)}: ${o.name}` : traceKindLabel(o.kind));

/**
 * The Profiling tab's filter bar: three multi-select filters (triggering event, template path, node
 * source) applied to both the Passes and Aggregate views.  Chip values are built from the stored
 * filter rather than the option list, so filters referencing since-evicted passes stay visible and
 * removable even after their option disappears.
 */
function ProfilingFilters({ filters, onChange, groups, shownCount }: Props) {
    const profiling = useAppSelector(s => s.profiling);
    const traces = useAppSelector(s => s.tracing.traces);

    const byLabel = (a: Option, b: Option) => a.label.localeCompare(b.label);

    const eventOptions = React.useMemo<Option[]>(
        () => eventFilterOptions(groups, traces, profiling.startedAtPerf ?? -Infinity)
            .map(o => ({ value: o, label: eventLabel(o) }))
            .sort(byLabel),
        [groups, traces, profiling.startedAtPerf]
    );
    const fileOptions = React.useMemo<Option[]>(
        () => fileFilterOptions(profiling).map(f => ({ value: f, label: f })).sort(byLabel),
        [profiling]
    );
    // One option per distinct source text (several source locations can carry identical text);
    // the label is the compact node label, the hover tooltip shows the full source.
    const sourceOptions = React.useMemo<Option[]>(() => {
        const bySource = new Map<string, string>();
        for (const row of aggregateCycles(Object.values(profiling.cyclesById))) {
            if (!bySource.has(row.source)) bySource.set(row.source, row.label);
        }
        return [...bySource.entries()].map(([source, label]) => ({ value: source, label })).sort(byLabel);
    }, [profiling]);
    const sourceLabels = React.useMemo(() => new Map(sourceOptions.map(o => [o.value as string, o.label])), [sourceOptions]);

    const eventValue = React.useMemo<Option[]>(() => filters.events.map(o => ({ value: o, label: eventLabel(o) })), [filters.events]);
    const fileValue = React.useMemo<Option[]>(() => filters.files.map(f => ({ value: f, label: f })), [filters.files]);
    const sourceValue = React.useMemo<Option[]>(
        () => filters.sources.map(s => ({
            value: s,
            label: sourceLabels.get(s) ?? nodeLabel({ componentName: undefined, nodeType: "JSXElement", source: s }),
        })),
        [filters.sources, sourceLabels]
    );

    const sourceTooltip = (option: Option) => <CodeBlock code={String(option.value)} maxHeight={160} fontSize="0.66rem" />;

    // Only min ⇒ "≥", only max ⇒ "≤", both ⇒ "between"; an empty field lifts that bound.
    const setWallTimeBound = (bound: "min" | "max") => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === "" ? null : Math.max(0, Number(e.target.value));
        onChange({ ...filters, wallTime: { ...filters.wallTime, [bound]: value } });
    };

    return (
        <div className="profiling-filterbar">
            <span>Filter by: </span>
            <div className="flex-1"></div>
            <Autocomplete
                multiple
                size="small"
                limitTags={2}
                label="Triggered by event"
                options={eventOptions}
                value={eventValue}
                onChange={(_e, v) => onChange({ ...filters, events: (v as Option[]).map(o => o.value) })}
            />
            <Autocomplete
                multiple
                size="small"
                limitTags={2}
                label="Template"
                options={fileOptions}
                value={fileValue}
                onChange={(_e, v) => onChange({ ...filters, files: (v as Option[]).map(o => o.value) })}
            />
            <Autocomplete
                multiple
                size="small"
                limitTags={2}
                label="Node source"
                options={sourceOptions}
                value={sourceValue}
                getOptionTooltip={sourceTooltip}
                onChange={(_e, v) => onChange({ ...filters, sources: (v as Option[]).map(o => o.value) })}
            />
            <div className="wall-time-filter" title="Show only passes whose elapsed (wall) time falls within these bounds. Fill in one bound for ≥ / ≤, both for a between range.">
                <TextField
                    label="Min wall time (ms)"
                    type="number"
                    slotProps={{ htmlInput: { min: 0 } }}
                    value={filters.wallTime.min ?? ""}
                    onChange={setWallTimeBound("min")}
                />
                <TextField
                    label="Max wall time (ms)"
                    type="number"
                    slotProps={{ htmlInput: { min: 0 } }}
                    value={filters.wallTime.max ?? ""}
                    onChange={setWallTimeBound("max")}
                />
            </div>
            <Button size="small" variant="outlined" disabled={!hasActiveFilters(filters)} title="Reset the event, template, node-source and wall-time filters" onClick={() => onChange(EMPTY_FILTERS)}>
                Clear filters
            </Button>
            {hasActiveFilters(filters) && (
                <span className="filter-count">
                    {shownCount} of {groups.length} pass{groups.length === 1 ? "" : "es"}
                </span>
            )}
        </div>
    );
}

export default React.memo(ProfilingFilters);
